import { UNGROUPED_RESOURCES_GROUP_ID } from '@/shared/features/groups/components/group-list/group-list.constants';
import {
  TGroupCrumbData,
  TGroupCrumbGroup,
} from '@/shared/features/groups/utils/group-hierarchy/group-crumbs.types';
import { GROUP_PATH_SEPARATOR } from '@/shared/features/groups/utils/group-path/elide-group-path';
import { TCrumb } from '@/shared/types/router.types';

// The outermost readable group is where the trail stops, so it alone spells out
// the ancestors the reader cannot open; every crumb below it is one name.
const getCrumbLabel = (group: TGroupCrumbGroup, isVisibleRoot: boolean) => {
  if (!isVisibleRoot || !group.fullPaths?.length) {
    return group.name;
  }

  return group.fullPaths.join(GROUP_PATH_SEPARATOR);
};

export const getGroupCrumbLabel = (data: TGroupCrumbData) => {
  if (typeof data === 'string' || !data?.group) {
    return undefined;
  }

  return getCrumbLabel(data.group, !data.ancestors?.length);
};

// Breadcrumb ancestry: the ungrouped crumb, else every readable ancestor.
export const getGroupAncestorCrumb = (
  data: TGroupCrumbData,
): TCrumb | undefined => {
  if (typeof data === 'string') {
    if (data !== UNGROUPED_RESOURCES_GROUP_ID) {
      return undefined;
    }

    return {
      label: 'ungrouped_resources',
      localized: true,
      link: `groups/${UNGROUPED_RESOURCES_GROUP_ID}`,
    };
  }

  const ancestors = data?.ancestors ?? [];

  const buildCrumb = (index: number): TCrumb => {
    const ancestor = ancestors[index];
    const isVisibleRoot = index === ancestors.length - 1;

    return {
      label: getCrumbLabel(ancestor, isVisibleRoot),
      link: `groups/${ancestor.groupId}`,
      ancestor: isVisibleRoot ? undefined : buildCrumb(index + 1),
    };
  };

  return ancestors.length ? buildCrumb(0) : undefined;
};
